import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useMessagingStore } from '@/store/messaging.store';
import { MessageItem } from './MessageItem';
import type { MessageWithDetails } from '@/types/messaging';

import FaIcon from '../common/FaIcon';
import Button from '../common/Button';
import EmptyState from '../common/EmptyState';
interface MessageListProps {
  conversationId: string;
  onForward?: (message: MessageWithDetails) => void;
}

export function MessageList({ conversationId, onForward }: MessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { messagesByConversation, pinnedMessages } = useMessagingStore();
  const [showJumpButton, setShowJumpButton] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const isNearBottomRef = useRef(true);
  const lastScrollHeightRef = useRef(0);

  const messages = messagesByConversation[conversationId] || [];
  const pinned = pinnedMessages[conversationId] || [];
  const messagesLengthRef = useRef(messages.length);
  messagesLengthRef.current = messages.length;

  // Handle scroll to detect if user is near bottom
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const nearBottom = distanceFromBottom < 100;

    isNearBottomRef.current = nearBottom;
    setIsNearBottom(nearBottom);
    setShowJumpButton(!nearBottom && messagesLengthRef.current > 5);
  }, []); // No dependencies - uses ref for messages length

  // Auto-scroll to bottom on new messages (only if already near bottom)
  useEffect(() => {
    if (containerRef.current && isNearBottomRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
    lastScrollHeightRef.current = containerRef.current?.scrollHeight || 0;
  }, [messages.length]);

  // Initial scroll to bottom
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [conversationId]);

  const scrollToBottom = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, []);

  // Memoized group messages by date
  const groupedMessages = useMemo(() => groupMessagesByDate(messages), [messages]);

  if (messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <EmptyState
          icon="message"
          title="No messages yet"
          description="Send a message to start the conversation"
          size="sm"
        />
      </div>
    );
  }

  return (
    <div className="relative h-full">
      {/* Pinned messages banner */}
      {pinned.length > 0 && (
        <div className="absolute top-0 left-0 right-0 z-10 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
          <Button
            variant="ghost"
            size="sm"
            className="w-full px-4 py-2 flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 justify-start min-h-0"
          >
            <FaIcon icon="thumbtack" className="w-4 h-4" />
            <span className="font-medium">{pinned.length} pinned message{pinned.length > 1 ? 's' : ''}</span>
            <span className="text-amber-600 dark:text-amber-400 truncate flex-1 text-left">
              - {pinned[0].content?.substring(0, 50)}...
            </span>
          </Button>
        </div>
      )}

      {/* Messages container */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className={`h-full overflow-y-auto px-4 py-2 space-y-1 ${pinned.length > 0 ? 'pt-12' : ''}`}
      >
        {groupedMessages.map((group) => (
          <div key={group.date}>
            {/* Date separator */}
            <div className="flex items-center justify-center my-4">
              <div className="bg-surface-tertiary text-theme-tertiary text-xs px-3 py-1 rounded-full">
                {group.dateLabel}
              </div>
            </div>

            {/* Messages for this date */}
            {group.messages.map((message, index) => {
              const prevMessage = index > 0 ? group.messages[index - 1] : null;
              const showSender = !prevMessage || prevMessage.sender_id !== message.sender_id;

              return (
                <MessageItem
                  key={message.id}
                  message={message}
                  showSender={showSender}
                  conversationId={conversationId}
                  onForward={onForward}
                />
              );
            })}
          </div>
        ))}
      </div>

      {/* Jump to latest button */}
      {showJumpButton && (
        <Button
          variant="primary"
          size="sm"
          icon={<FaIcon icon="arrow-down" className="w-4 h-4" />}
          onClick={scrollToBottom}
          className="absolute bottom-4 right-4 rounded-full shadow-lg"
        >
          Jump to latest
        </Button>
      )}
    </div>
  );
}

interface MessageGroup {
  date: string;
  dateLabel: string;
  messages: MessageWithDetails[];
}

function groupMessagesByDate(messages: MessageWithDetails[]): MessageGroup[] {
  const groups: MessageGroup[] = [];
  let currentDate = '';

  for (const message of messages) {
    const messageDate = new Date(message.created_at).toDateString();

    if (messageDate !== currentDate) {
      currentDate = messageDate;
      groups.push({
        date: messageDate,
        dateLabel: formatDateLabel(new Date(message.created_at)),
        messages: [],
      });
    }

    groups[groups.length - 1].messages.push(message);
  }

  return groups;
}

function formatDateLabel(date: Date): string {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  }

  if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }

  return date.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}
